/* eslint-disable @typescript-eslint/no-explicit-any */
import { v4 as uuid } from 'uuid';
import { cookie } from '@turnercode/cdp-cookie';
import { findFrame } from './privacy';
import { cookiesEnabled } from './utils/browser';
import { CCPA_LOCATIONS, URLS } from './utils/constants';
import { info } from './utils/logger';
import { sendRequest } from './utils/sendRequest';

export type WmukidValue =
  | string
  | {
      id: string;
      version: string;
      timestamp: string;
    };

export type CdpidValue = string | { cdpId: string; wmukId: string };

const win = window as any;
const doc = document as any;

/**
 * Initializes the Prism identity module, which is responsible for:
 *   - Managing the WMUKID
 *   - Capturing third-party IDs
 *   - Sending data to Doppler
 *
 * @param core The Prism core payload and API manager
 * @param cb Function invoked after tracking an identity registration
 */
export function initIdentity(env: string, countryCode: string): { wmukid: string; cdpid: string } {
  if (!CCPA_LOCATIONS.includes(countryCode)) {
    return { wmukid: 'Unknown', cdpid: 'Unknown' };
  }

  let wmukid: WmukidValue = '';
  let wmukidStable = '';
  let cdpid: CdpidValue = '';

  function updateCdpData(cdpId: string, wmukId: string) {
    cookie.set('CDPID', JSON.stringify({ cdpId, wmukId }), {
      samesite: 'None',
      secure: true,
      encode: false,
    });
    if (cdpId && wmukId) {
      sendRequest(URLS.cdpSvc[env], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ v: 1, action: 1, wmukId, cdpId }),
      });
    }
  }

  if (cookiesEnabled()) {
    wmukid = cookie.get('WMUKID') as WmukidValue;
    wmukidStable = cookie.get('WMUKID_STABLE') as string;
    cdpid = cookie.get('CDPID') as CdpidValue;

    info({
      message: `[PSM]: Cookies enabled. Retrieving ID values
WMUKID: ${JSON.stringify(wmukid)}
WMUKID_STABLE: ${wmukidStable}
CDPID: ${JSON.stringify(cdpid)}
`,
      methodName: 'initIdentity',
      eventType: 'identity',
    });

    if (wmukidStable == null) {
      if (wmukid == null) {
        // If the WMUKID does not exist, generate one, set a cookie, and track the newly-registered ID
        wmukidStable = uuid();
        info({
          message: `WMUKID and WMUKID_STABLE cookies not found. Generating WMUKID_STABLE value: ${wmukidStable}`,
          methodName: 'initIdentity',
          eventType: 'identity',
        });
      } else if (typeof wmukid === 'object' && 'id' in wmukid) {
        // If a legacy WMUKID cookie exists, pull the ID value from it
        wmukidStable = wmukid.id;
        info({
          message: `Legacy WMUKID value ${wmukid.id} found. Setting WMUKID_STABLE value: ${wmukidStable}`,
          methodName: 'initIdentity',
          eventType: 'identity',
        });
      } else {
        wmukidStable = wmukid;
        info({
          message: `WMUKID string value ${wmukid} found. Setting WMUKID_STABLE value: ${wmukidStable}`,
          methodName: 'initIdentity',
          eventType: 'identity',
        });
      }
    }

    info({
      message: `Setting WMUKID_STABLE cookie to ${wmukidStable}. Cookie options: ${JSON.stringify(cookie.options())}`,
      methodName: 'initIdentity',
      eventType: 'identity',
    });
    cookie.set('WMUKID_STABLE', wmukidStable);

    if (cdpid == null) {
      if (findFrame('prism_toolkit') === null) {
        const iframe = doc.createElement('iframe');
        iframe.setAttribute('id', 'prism_toolkit');
        iframe.setAttribute('style', 'display:none');
        iframe.setAttribute('src', URLS.iFrameOrigin + URLS.iFramePath[env]);
        doc.body.appendChild(iframe);

        win.addEventListener(
          'message',
          (e: MessageEvent) => {
            if (e.origin === URLS.iFrameOrigin) {
              info({
                message: `Updating CDPID from iFrame value: ${JSON.stringify(e.data)}`,
                methodName: 'initIdentity',
                eventType: 'message',
              });
              updateCdpData(e.data, wmukidStable);
            }
          },
          false
        );
      }
    } else if (typeof cdpid === 'object' && 'cdpId' in cdpid && 'wmukId' in cdpid) {
      const cdpidStr: string = cdpid.cdpId;
      const wmukidStr: string = cdpid.wmukId;

      if (wmukidStable !== wmukidStr) {
        info({
          message: `Updating CDPID value ${cdpidStr} with WMUKID: ${wmukidStable}`,
          methodName: 'initIdentity',
          eventType: 'cdpid',
        });
        updateCdpData(cdpidStr, wmukidStable);
      } else {
        info({
          message: `CDPID cookie has already been set to value ${JSON.stringify(
            cdpid
          )}. To start a new test, delete the CDPID cookie on this domain and lightning.warnermediacdn.com.`,
          methodName: 'initIdentity',
          eventType: 'cdpid',
        });
      }
    }
  }

  return { wmukid: wmukidStable, cdpid: cdpid as string };
}
